import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import {
  type TestSummaryItem,
  mapSuiteRunSummariesWithRunNumbers,
  mapTestRunSummariesWithRunNumbers,
  TableNameCell,
} from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { EventOutlinedIcon, PersonOutlineOutlinedIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { ResultsFileDto } from '@flowqa/shared';

const columnWidths = tableColumnWidths.resultsFiles;

const mapExecutionRunSummaries = (
  summaries: ResultsFileDto['executionRunSummaries'],
): TestSummaryItem[] =>
  (summaries ?? []).map((summary) => {
    const executionHref = `/execution-runs/${summary.executionRunId}`;
    return {
      testId: summary.executionRunId,
      testName: summary.executionRunName ?? summary.executionRunId,
      href: executionHref,
      executionHref,
      tooltipLabel: summary.executionRunName ?? summary.executionRunId,
      status: summary.status ?? null,
      executionId: summary.executionRunId,
      timestamp: summary.timestamp ?? null,
    };
  });

export const buildResultsFilesColumns = ({
  editRowPendingId = null,
  deleteRowPendingId = null,
  restoreRowPendingId = null,
  onEditRow,
  onDeleteRow,
  onRestoreRow,
  tagColorByName: _tagColorByName = {},
  updateRowTagsColorPendingId = null,
}: {
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
  restoreRowPendingId?: string | null;
  onEditRow?: (row: ResultsFileDto) => void;
  onDeleteRow?: (row: ResultsFileDto) => void;
  onRestoreRow?: (row: ResultsFileDto) => void;
  tagColorByName?: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId?: string | null;
} = {}): Array<GridColDef<ResultsFileDto>> => [
  {
    field: 'fileName',
    headerName: 'File',
    flex: columnWidths.file.flex,
    cellClassName: 'table-name-cell',
    valueGetter: (_value, row) => row.fileName,
    renderCell: (params) => {
      const tags = params.row.deletedAt ? [{ label: 'Deleted', tone: 'red' as const }] : [];
      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label: params.row.fileName,
              href: `/results-files/${params.row.slug}`,
            }}
            secondary={{
              label: params.row.uploadId,
              href: `/uploads/${params.row.uploadId}`,
            }}
            tags={tags}
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'executionRunPassed',
    headerName: 'Execution Runs',
    flex: columnWidths.executionRuns.flex,
    sortable: false,
    filterable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="execution"
            counts={{
              passed: params.row.executionRunPassed ?? 0,
              failed: params.row.executionRunFailed ?? 0,
              errored: params.row.executionRunErrored ?? 0,
              skipped: params.row.executionRunSkipped ?? 0,
              untested: params.row.executionRunUntested ?? 0,
            }}
            summaryItems={() => mapExecutionRunSummaries(params.row.executionRunSummaries)}
            summaryUnitLabel="execution"
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'suiteRunPassed',
    headerName: getDetailLabel('suites'),
    flex: columnWidths.suites.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="suite"
            counts={{
              passed: params.row.suiteRunPassed ?? 0,
              failed: params.row.suiteRunFailed ?? 0,
              errored: params.row.suiteRunErrored ?? 0,
              skipped: params.row.suiteRunSkipped ?? 0,
              untested: params.row.suiteRunUntested ?? 0,
            }}
            summaryItems={() => mapSuiteRunSummariesWithRunNumbers(params.row.suiteRunSummaries)}
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'testRunPassed',
    headerName: getDetailLabel('tests'),
    flex: columnWidths.tests.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="test"
            counts={{
              passed: params.row.testRunPassed ?? 0,
              failed: params.row.testRunFailed ?? 0,
              errored: params.row.testRunErrored ?? 0,
              skipped: params.row.testRunSkipped ?? 0,
              untested: params.row.testRunUntested ?? 0,
            }}
            summaryItems={() =>
              mapTestRunSummariesWithRunNumbers(params.row.testRunSummaries, 'full')
            }
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'details',
    headerName: getDetailLabel('details'),
    flex: columnWidths.details.flex,
    align: 'left',
    headerAlign: 'left',
    sortable: false,
    filterable: false,
    cellClassName: 'table-meta-details-cell',
    renderCell: (params) => {
      const uploadedByLabel = params.row.createdByEmail ?? params.row.createdByName ?? '-';
      const uploadedAt = params.row.createdAt;
      const uploadedLabel = formatRelativeTime(uploadedAt);
      const uploadedTitle = formatDate(uploadedAt, { includeTime: true });

      return (
        <TableCellContainer gap="none">
          <TableBreakdownStack>
            <TableBreakdownLine
              rowTitle="Uploaded By"
              icon={
                <PersonOutlineOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
              }
              content={uploadedByLabel}
            />
            <TableBreakdownLine
              rowTitle="Uploaded"
              icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              content={uploadedLabel && uploadedLabel.length > 0 ? uploadedLabel : '-'}
              valueTitle={uploadedTitle}
              contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
              valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
            />
          </TableBreakdownStack>
        </TableCellContainer>
      );
    },
  },
  {
    field: 'actions',
    headerName: '',
    flex: columnWidths.actions.flex,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams<ResultsFileDto>) => (
      <TableActionsCell
        rowName={params.row.fileName}
        editActionLabel={getEntityActionLabel('edit', 'Results File')}
        deleteActionLabel={getEntityActionLabel('delete', 'Results File')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        disableEdit={
          editRowPendingId === params.row.id ||
          deleteRowPendingId === params.row.id ||
          restoreRowPendingId === params.row.id ||
          updateRowTagsColorPendingId === params.row.id
        }
        onDelete={
          params.row.deletedAt
            ? undefined
            : () => {
                onDeleteRow?.(params.row);
              }
        }
        onRestore={
          params.row.deletedAt
            ? () => {
                onRestoreRow?.(params.row);
              }
            : undefined
        }
        disableDelete={deleteRowPendingId === params.row.id}
        disableRestore={restoreRowPendingId === params.row.id}
        restoreActionLabel={getEntityActionLabel('restore', 'Results File')}
      />
    ),
  },
];
